import { Fragment, useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { CARD_SORTS, DAY, DECK_PAGE, cardOutcome, cardTypeName, fmt2, fmtStamp, playCounter, sgn2, sgnR, sortCards, type CardSort } from '@balansa/shared';
import { imageUrl } from '../lib/api';
import { IconEdit } from '../lib/icons';
import { useApp, useWheel } from '../store';
import { GameCard } from './GameCard';

export function Deck() {
  const { wheel, idx, en } = useWheel();
  const deckFilter = useApp((s) => s.ui.deckFilter);
  const A = useApp.getState;
  const f = deckFilter && idx.node.has(deckFilter) ? deckFilter : '';
  const sort: CardSort = wheel.settings.cardSort ?? 'added-desc';
  const cards = sortCards(
    wheel.cards.filter((c) => !f || c.typeId === f),
    idx,
    sort
  );
  const wk = Date.now() - 7 * DAY;
  const used = idx.tops.filter((t) => wheel.cards.some((c) => c.typeId === t));

  // Rāda pa DECK_PAGE kārtīm (“Ielādēt vēl”); mainot filtru vai secību — atkal no sākuma.
  const [limit, setLimit] = useState(DECK_PAGE);
  useEffect(() => setLimit(DECK_PAGE), [f, sort]);
  // Jauna kārts (no redaktora, dublēšanas vai AI aģenta), kas nonāk aiz redzamās daļas, tiek parādīta.
  const known = useRef<Set<string> | null>(null);
  useEffect(() => {
    const prev = known.current;
    known.current = new Set(wheel.cards.map((c) => c.id));
    if (!prev) return;
    const fresh = cards.flatMap((c, i) => (prev.has(c.id) ? [] : [i]));
    if (!fresh.length) return;
    // Pilna nomaiņa (imports, atiestatīšana) vai liela partija — sākam atkal no pirmās lapas.
    if (fresh.length === wheel.cards.length || fresh.length > DECK_PAGE) return setLimit(DECK_PAGE);
    const pos = Math.max(...fresh);
    if (pos >= limit) setLimit(Math.ceil((pos + 1) / DECK_PAGE) * DECK_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wheel.cards]);
  const visible = cards.slice(0, limit);
  const left = cards.length - visible.length;
  const cat = (c: { typeId: string | null }) => (c.typeId && idx.node.has(c.typeId) ? c.typeId : null);
  const groupCount = (k: string | null) => cards.filter((c) => cat(c) === k).length;

  const tilt = (e: PointerEvent<HTMLDivElement>) => {
    const g = (e.target as Element).closest<HTMLElement>('.gcard');
    if (!g || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const r = g.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    g.style.setProperty('--rx', ((0.5 - y) * 9).toFixed(2) + 'deg');
    g.style.setProperty('--ry', ((x - 0.5) * 11).toFixed(2) + 'deg');
    g.style.setProperty('--hx', (x * 100).toFixed(1) + '%');
    g.style.setProperty('--hy', (y * 100).toFixed(1) + '%');
  };
  const untilt = (e: PointerEvent<HTMLDivElement>) => {
    const g = (e.target as Element).closest<HTMLElement>('.gcard');
    if (g && !g.contains(e.relatedTarget as Node)) ['--rx', '--ry', '--hx', '--hy'].forEach((k) => g.style.removeProperty(k));
  };

  return (
    <section className="card deck-wrap" aria-label="Aktivitāšu kārtis">
      <div className="deck-head">
        <div>
          <h2>Aktivitāšu kārtis</h2>
          <p>
            Izspēlē kārti, kad aktivitāte notikusi. Tās ietekme uz jomām un enerģiju ir atkarīga no tava stāvokļa tajā brīdī — iezīmētie nosacījumi ir aktīvi
            tieši tagad.
          </p>
        </div>
        <div className="deck-actions">
          <select className="inp" aria-label="Filtrēt kārtis pēc jomas" value={f} onChange={(e) => A().setUi({ deckFilter: e.target.value })}>
            <option value="">Visas kārtis ({wheel.cards.length})</option>
            {used.map((t) => (
              <option key={t} value={t}>
                {idx.node.get(t)!.name} ({wheel.cards.filter((c) => c.typeId === t).length})
              </option>
            ))}
          </select>
          <select className="inp" aria-label="Kārtot kārtis" value={sort} onChange={(e) => A().setSettings({ cardSort: e.target.value as CardSort })}>
            {CARD_SORTS.map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
          <button type="button" className="btn" onClick={() => A().setPrinting(true)}>
            Drukāt kārtis
          </button>
          <button type="button" className="btn primary" onClick={() => A().openDialog({ kind: 'card', cardId: null })}>
            + Jauna kārts
          </button>
        </div>
      </div>
      <div className="deck-body">
        <div className="deck" id="deck" onPointerMove={tilt} onPointerOut={untilt}>
          {visible.map((c, i) => {
            const head =
              sort === 'category' && (i === 0 || cat(visible[i - 1]) !== cat(c)) ? (
                <div
                  className="dgroup"
                  role="heading"
                  aria-level={3}
                  data-testid="deck-group"
                  style={{ '--c': cat(c) ? idx.color(cat(c)!) : 'var(--ink-3)' } as CSSProperties}
                >
                  {cardTypeName(c, idx)} <span>{groupCount(cat(c))}</span>
                </div>
              ) : null;
            const n = wheel.journal.filter((j) => j.cardId === c.id && j.ts >= wk).length;
            const prior = playCounter(wheel.journal, c.id, Date.now());
            const o = cardOutcome(c, idx, en, prior);
            const mod = Math.abs(o.energyDelta - c.energy) >= 0.5 || Math.abs(o.aMul - 1) > 0.001 || o.stacks.some((s) => s.active);
            return (
              <Fragment key={c.id}>
                {head}
                <div
                  className="dcard"
                  data-dcard={c.id}
                  tabIndex={0}
                  role="button"
                  aria-label={'Izspēlēt kārti ' + c.name}
                  onClick={(e) => {
                    if ((e.target as Element).closest('.dedit')) return;
                    A().openDialog({ kind: 'play', cardId: c.id });
                  }}
                  onKeyDown={(e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      A().openDialog({ kind: 'play', cardId: c.id });
                    }
                  }}
                >
                  <GameCard card={c} idx={idx} en={en} live prior={prior} number={wheel.cards.indexOf(c) + 1} />
                  <div className="dmeta">
                    <span className={'dlive' + (mod ? ' mod' : '')}>
                      Tagad: <b className={o.energyDelta >= 0 ? 'pos' : 'neg'}>{sgnR(o.energyDelta)} ⚡</b>
                      {Math.abs(o.aMul - 1) > 0.001 ? ' · ietekme ×' + fmt2(o.aMul) : ''}
                      {o.stacks.some((s) => s.active) ? (
                        <span className="dstack" title="Krāšanās bonuss būs piemērots">
                          {' '}
                          · ⟳ bonuss
                        </span>
                      ) : null}
                    </span>
                    {n ? (
                      <span className="dcount" title="Izspēlēta pēdējās 7 dienās">
                        {n}× 7 d.
                      </span>
                    ) : null}
                  </div>
                  <button type="button" className="dedit icon-btn" title="Rediģēt kārti" aria-label="Rediģēt kārti" onClick={() => A().openDialog({ kind: 'card', cardId: c.id })}>
                    <IconEdit />
                  </button>
                </div>
              </Fragment>
            );
          })}
          <button type="button" className="dnew" onClick={() => A().openDialog({ kind: 'card', cardId: null })}>
            <span>+</span>Jauna kārts
          </button>
          {left > 0 ? (
            <div className="deck-more">
              <button type="button" className="btn" data-testid="deck-more" onClick={() => setLimit((l) => l + DECK_PAGE)}>
                Ielādēt vēl ({Math.min(DECK_PAGE, left)})
              </button>
              <small data-testid="deck-shown">
                Rāda {visible.length} no {cards.length}
              </small>
            </div>
          ) : null}
        </div>
        <aside className="journal" aria-label="Žurnāls">
          <h3 className="h3" style={{ marginTop: 0 }}>
            Žurnāls
          </h3>
          <Journal />
        </aside>
      </div>
    </section>
  );
}

function Journal() {
  const { wheel } = useWheel();
  const jAll = useApp((s) => s.ui.jAll);
  const A = useApp.getState;
  const all = wheel.journal.slice().reverse();
  const wk = all.filter((j) => j.ts >= Date.now() - 7 * DAY);
  const list = all.slice(0, jAll ? 500 : 12);
  return (
    <div id="journal" data-testid="journal">
      <div className="jsum">
        {wk.length ? (
          <>
            Pēdējās 7 dienās: <b>{wk.length}</b> {wk.length === 1 ? 'kārts' : 'kārtis'} · enerģija kopā <b>{sgnR(wk.reduce((a, j) => a + j.energyDelta, 0))} v.</b>
          </>
        ) : (
          'Vēl nav izspēlētu kāršu. Noklikšķini uz kārts, kad aktivitāte notikusi.'
        )}
      </div>
      {list.length ? (
        <ul className="jlist">
          {list.map((j) => (
            <li key={j.id}>
              <span className="jthumb" style={{ '--tc': j.color } as React.CSSProperties}>
                {j.name.charAt(0).toUpperCase()}
                {j.image ? <img src={imageUrl(j.image)} alt="" loading="lazy" onError={(e) => e.currentTarget.remove()} /> : null}
              </span>
              <div className="jmain">
                <div className="jt">
                  <b>{j.name}</b>
                  <span>{fmtStamp(j.ts)}</span>
                </div>
                <div className="js">
                  ⚡ {sgnR(j.energyDelta)} ({Math.round(j.Lbefore)} → {Math.round(j.Lafter)} v.)
                  {j.effects.length ? ' · ' + j.effects.map((e) => e.name + ' ' + sgn2(e.delta)).join(', ') : ''}
                </div>
                {j.rules.length ? <div className="jr">{j.rules.join('; ')}</div> : null}
                {j.stacks?.length ? <div className="jr jstk">⟳ {j.stacks.join('; ')}</div> : null}
                {j.note ? <div className="jn">{j.note}</div> : null}
              </div>
              <button
                type="button"
                className="icon-btn"
                title="Atsaukt šo ierakstu"
                aria-label="Atsaukt"
                onClick={async () => {
                  const ok = await A().ask({
                    title: 'Atsaukt “' + j.name + '”?',
                    message: fmtStamp(j.ts) + ' — jomu vērtības un enerģija tiks atgrieztas.',
                    ok: 'Atsaukt'
                  });
                  if (ok) await A().undoEntry(j.id);
                }}
              >
                ↶
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {all.length > 12 ? (
        <button type="button" className="btn sm ghost jmore" onClick={() => A().setUi({ jAll: !jAll })}>
          {jAll ? 'Rādīt mazāk' : 'Rādīt visus (' + all.length + ')'}
        </button>
      ) : null}
    </div>
  );
}
